import React, {useContext, useEffect, useState} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import PropTypes from 'prop-types';

import {Link as ExternalLink, Sprite, Typography} from '@nur-portal/ui-kit';

import {LoginTracker} from '../../../../services/analytics/auth';
import {CommentsEventLogger} from '../../../../services/analytics/comments';
// Components
import {Button} from '../../../../website-ui/components/button';
import CommentForm from '../../../../website-ui/components/comments/comment-form-area/comment-form';
import CommentHighlightArea
from '../../../../website-ui/components/comments/comment-highlight-area/comment-highlight-area';
import CommentThread from '../../../../website-ui/components/comments/comment-thread/comment-thread';
import Link from '../../../../website-ui/components/link';
import Loader from '../../../../website-ui/components/loader';
import {USER_ROLES} from '../../../../website-ui/constants/user';
import {NetworkStatusContext} from '../../../../website-ui/contexts/network-status-provider';
import {ReactionsProvider} from '../../../../website-ui/contexts/reactions-provider';
import {useUser} from '../../../../website-ui/hooks/use-user';
import {readArticleThreadComments} from '../../../../website-ui/use-cases/comments/read-article-thread-comments';
// Tools
import {saveComment} from '../../../../website-ui/use-cases/comments/save-comment';
import {useArticleModel} from '../../../../website-ui/utils/shared/useArticleModel';
import {useCommentsFromMemory} from '../../../../website-ui/utils/shared/useCommentsFromStore';

import {AuthenticatedOverlay} from './authenticated-overlay';
import {CloseIcon} from './close-icon';
import Comment from './comment';

// Styles
import styles from './modal-comments.module.css';

const LIMIT_COMMENTS_CODE = 429;
const LOGIN_LINK_URL = `${window.location.origin}/login?redirectURL=${window.location.pathname}`;
const COMMENT_RULES_LINK = `${window.location.origin}/comment-rules.html`;

const ModalComments = ({onClose, articleId}) => {
  const {t} = useTranslation();
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [isCommentLoading, setCommentLoading] = useState(false);
  const [modalCommentsData, setModalCommentsData] = useState({
    status: 'loading',
    error: ''
  });
  const {commentsFromMemory} = useCommentsFromMemory();
  const [saveCommentError, setSaveCommentError] = useState('');
  const user = useUser();

  const article = useArticleModel(articleId);
  const isOnline = useContext(NetworkStatusContext);

  const isUserAbleToComment = user.isAuthenticated
      && user.role === USER_ROLES.EXTENDED
      && Boolean(user.username);

  useEffect(() => {
    readArticleThreadComments(articleId)
      .then(() => setModalCommentsData({
        status: 'success',
        error: ''
      }))
      .catch(e => setModalCommentsData({
        status: 'error',
        error: e
      }));
  }, []);

  const handleSaveComment = currentUser => commentText => {
    if (!isOnline) {
      return setSaveCommentError(t('articleComments.connectionError'));
    }

    setCommentLoading(true);

    saveComment(commentText, null, article, currentUser)
      .then(data => {
        if (!data.isSucces && data.tokenError) {
          return;
        }

        if (!data.isSucces) {
          const errorText = data?.code === LIMIT_COMMENTS_CODE
            ? t('articleComments.limitError')
            : t('articleComments.publishError');

          setSaveCommentError(errorText);
        } else {
          setShowCommentForm(false);
          setSaveCommentError('');
        }
      })
      .finally(() => {
        setCommentLoading(false);
      });
  };

  const handleClickResetButton = () => {
    setShowCommentForm(false);
    setSaveCommentError('');
  };

  const handleClickInputButton = () => setShowCommentForm(true);

  const logErrorModalEvent = e => {
    if (window.history.state?.id) {
      CommentsEventLogger.openFromCommentsButtonError(e, articleId);
    } else {
      CommentsEventLogger.openFromUrlError(e, articleId);
    }
  };

  useEffect(() => {
    if (modalCommentsData.status !== 'loading') {
      CommentsEventLogger.showCommentsModal(articleId);
      if (modalCommentsData.status === 'error') {
        logErrorModalEvent(modalCommentsData.error);
      }
    }
  }, [modalCommentsData]);

  const commentForm = (
    !showCommentForm ? (
      <div
        className={styles.inputButton}
        onClick={handleClickInputButton}
      >
        <Typography className={styles.inputButtonText}>
          {t('articleComments.inputText')}
        </Typography>
      </div>
    ) : (
      <CommentForm
        onSubmit={handleSaveComment(user)}
        onReset={handleClickResetButton}
        error={saveCommentError}
        type="main"
        articleId={articleId}
        isLoading={isCommentLoading}
      />
    )
  );

  const handleCloseButtonClick = () => {
    CommentsEventLogger.clickCloseButton(articleId);
    onClose();
  };

  const handleAuthButtonClick = () => {
    LoginTracker.fromCommentsModal();
  };

  return (
    <div className={styles.wrapper}>
      {!article
        ? <Loader />
        : (
          <>
            <div className={styles.header}>
              <div className={styles.headerControls}>
                <Typography
                  variant='title-21'
                >
                  {t('articleComments.modalComments')} {article.commentsCount || 0}
                </Typography>
                <Button
                  className={styles.closeButton}
                  onClick={handleCloseButtonClick}
                >
                  <CloseIcon />
                </Button>
              </div>
              <CommentHighlightArea>
                <Typography variant='title-14'>
                  {article.headline}
                </Typography>
              </CommentHighlightArea>
              {isUserAbleToComment && (
                <Typography variant="text-14">
                  <Trans
                    i18nKey='articleComments.modalAgreement'
                    components={{
                      text: (
                        <ExternalLink
                          isExternalLink
                          href={COMMENT_RULES_LINK}
                          rel="nofollow"
                          target="_blank"
                        />)
                    }}
                  />
                </Typography>)
              }
              {user.isAuthenticated ? (
                <AuthenticatedOverlay />
              ) : (
                <div className={styles.greyArea}>
                  <Typography variant='text-14'>
                    <Trans
                      i18nKey="articleComments.notAuthenticatedText"
                      components={{
                        text: (
                          <Link
                            href={LOGIN_LINK_URL}
                            onClick={handleAuthButtonClick}
                            className={styles.linkText}
                            rel="nofollow"
                          />)
                      }}
                    />
                  </Typography>
                </div>
              )}
              {isUserAbleToComment ? commentForm : null}
            </div>
            <ReactionsProvider articleId={articleId}>
              <div className={styles.comments}>
                {commentsFromMemory.length >= 1 ? commentsFromMemory.map((comment, index) => (
                  <div key={comment.id} className={styles.commentThreadWrapper}>
                    <Comment
                      articleId={articleId}
                      comment={comment}
                      className={styles.rootComment}
                      level={1}
                      isLast={index === commentsFromMemory.length - 1 && comment.replies.length === 0}
                    />
                    {comment.replies.length >= 1 ? comment.replies.map(commentReply => (
                      <div className={styles.rootReply} key={commentReply.id}>
                        <CommentThread
                          articleId={articleId}
                          comment={commentReply}
                          level={2}
                          isLast={index === commentsFromMemory.length - 1}
                        />
                      </div>
                    )) : null}
                  </div>
                )) : null}
                {modalCommentsData.status === 'loading' ? <Loader /> : null}
              </div>
            </ReactionsProvider>
            <Sprite />
          </>
        )
      }
    </div>

  );
};

ModalComments.propTypes = {
  articleId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired
};

export default ModalComments;
